import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { Error as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { isChunkLoadError } from '../utils/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** When any value in this list changes, a caught error is cleared automatically. */
  resetKeys?: Array<string | number | boolean | null | undefined>;
}

interface State {
  hasError: boolean;
  error?: Error;
  remountKey: number;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, remountKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys } = this.props;
    if (!this.state.hasError || !resetKeys || resetKeys.length === 0) {
      return;
    }

    const prevKeys = prevProps.resetKeys ?? [];
    const keysChanged = resetKeys.some((key, index) => key !== prevKeys[index])
      || resetKeys.length !== prevKeys.length;

    if (keysChanged) {
      this.setState((state) => ({
        hasError: false,
        error: undefined,
        remountKey: state.remountKey + 1,
      }));
    }
  }

  handleRetry = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }

    this.setState((state) => ({
      hasError: false,
      error: undefined,
      remountKey: state.remountKey + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const chunkLoadError = isChunkLoadError(this.state.error);

      return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper
            elevation={3}
            sx={(theme) => ({
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              color: 'text.primary',
              bgcolor: 'background.paper',
              background:
                theme.palette.mode === 'light'
                  ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                  : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, rgba(255,255,255,0.04) 100%)`,
              border: `1px solid ${
                theme.palette.mode === 'light'
                  ? 'rgba(244, 67, 54, 0.1)'
                  : 'rgba(244, 67, 54, 0.28)'
              }`,
            })}
          >
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Oops! Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
            </Typography>
            {chunkLoadError ? (
              <Typography variant="body2" color="text.secondary" paragraph>
                This can happen after an app update when your browser is still using an older cached version.
              </Typography>
            ) : null}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: 'error.light',
                  borderRadius: 1,
                  textAlign: 'left',
                }}
              >
                <Typography variant="caption" component="pre" sx={{ color: 'error.contrastText' }}>
                  {this.state.error.toString()}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
              sx={{
                mt: 3,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
                },
              }}
            >
              {chunkLoadError ? 'Reload page' : 'Try Again'}
            </Button>
          </Paper>
        </Container>
      );
    }

    return (
      <React.Fragment key={this.state.remountKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default ErrorBoundary;
